<template>
	<v-card class="pa-2">
		<h2>
			{{ title }}
		</h2>
		<Doughnut
			v-if="isPopulated"
			:chart-options="chartOptions"
			:chart-data="chartData"
			:chart-id="chartId"
			:dataset-id-key="datasetIdKey"
			:plugins="plugins"
			:css-classes="cssClasses"
			:styles="styles"
			:width="width"
			:height="height"
		/>
		<!-- `ghost-shape` is not optional here. It drives the loading skeleton as
		well as the empty and error scaffold, and its default is a bar chart, so
		leaving it off would have this card pulse and sit empty as bars while
		rendering a doughnut when populated. -->
		<ChartState
			v-else
			:variant="chartState"
			:height="height"
			ghost-shape="pie"
			v-on="$listeners"
		/>
		<template v-if="isPopulated">
			<h3 class="mt-2">
				Total:
				<span class="font-weight-light">
					{{ getTotal }}
				</span>
			</h3>
			<hr>
		</template>
		<div
			v-show="isPopulated"
			:id="containerId"
		/>
	</v-card>
</template>

<script>
import { Doughnut } from 'vue-chartjs/legacy'

import {
	Chart as ChartJS,
	Title,
	Tooltip,
	Legend,
	ArcElement,
	CategoryScale
} from 'chart.js'
import htmlLegendPluginMixin from '../mixins/html-legend-plugin.js'
import ChartState from './chart/chart-state.vue'
import { resolveChartState } from '../utils/chart-data-state.js'

export default {
	name: 'doughnut-chart',
	components: {
		Doughnut,
		ChartState
	},
	mixins: [htmlLegendPluginMixin],
	props: {
		chartId: {
			type: String,
			default: 'doughnut-chart'
		},
		datasetIdKey: {
			type: String,
			default: 'label'
		},
		width: {
			type: Number,
			default: 400
		},
		height: {
			type: Number,
			default: 300
		},
		cssClasses: {
			default: '',
			type: String
		},
		styles: {
			type: Object,
			default: () => {}
		},
		plugins: {
			type: Array,
			default: () => []
		},
		chartData: {
			type: Object
		},
		chartOptions: {
			type: Object
		},
		containerId: {
			type: String
		},
		title: {
			type: String
		},
		/** Whether the host is still fetching. Shows the loading state. */
		loading: {
			type: Boolean,
			default: false
		},
		/**
		 * Any truthy value marks the fetch as failed and shows the error state
		 * rather than the empty one.
		 */
		error: {
			type: [String, Object, Boolean, Error],
			default: null
		}
	},
	computed: {
		/**
		 * Doughnut arcs are sized from the values alone, so labels are legend
		 * text here rather than layout and are not required.
		 */
		chartState () {
			return resolveChartState({
				loading: this.loading,
				error: this.error,
				chartData: this.chartData,
				requireLabels: false
			})
		},
		isPopulated () {
			return this.chartState === 'populated'
		},
		getTotal () {
			// Sums every dataset, matching what hasPlottableData inspects, so
			// the card cannot render as populated while reporting "Total: 0".
			// Optional chaining is deliberate too: this used to read
			// datasets[0].data unguarded and threw on an absent payload.
			const datasets = this.chartData?.datasets || []
			return datasets.reduce((total, dataset) => {
				const data = dataset?.data || []
				return total + data.reduce((acc, val) => acc + (Number(val) || 0), 0)
			}, 0)
		}
	},
	created () {
		// Set global font defaults for Chart.js
		ChartJS.defaults.font.family = 'Inter, sans-serif'
		ChartJS.defaults.font.size = 12
		ChartJS.defaults.font.weight = '400'

		ChartJS.register(Title, Tooltip, Legend, ArcElement, CategoryScale, this.htmlLegendPlugin(this.containerId))
	},
	mounted () {
		ChartJS.unregister(this.htmlLegendPlugin(this.containerId))
	}
}
</script>

<style scoped>
h2 {
  text-align: center;
  transform: translateY(149px);
  line-height: 0;
  font-size: 1.5rem;
}

h3 {
	font-size: 1.2rem;
}

@media only screen and (max-width: 1400px) {
  h2 {
    font-size: 1.2rem
  }
}
@media only screen and (max-width: 1200px) {
  h2 {
    font-size: 1rem
  }
}

</style>
